<template>
  <ContentSection>
    <Container tag="div" class="mb-32">
      <h2 class="mb-9 font-semibold typo-h3">
        {{ title }}
      </h2>
      <AnimatedContainer>
        <ul
          v-if="singleDepartment && data.entries.length"

          class="
            flex flex-col gap-x-default gap-y-default
            sm:grid sm:grid-cols-[repeat(auto-fill,_minmax(17.5rem,_1fr))]
          "
        >
          <li v-for="entry in data.entries" :key="entry.id">
            <CardTeamMember :data="entry" />
          </li>
        </ul>
      </AnimatedContainer>
      <ul
        v-if="!singleDepartment && groupedTeamMembers.length"
        class="space-y-24"
      >
        <li v-for="department in groupedTeamMembers" :key="department.id">
          <h3 class="mb-9 font-semibold typo-h4">
            {{ department.title }}
          </h3>
          <AnimatedContainer>
            <ul
              v-if="department"

              class="
                flex flex-col gap-x-default gap-y-default
                sm:grid sm:grid-cols-[repeat(auto-fill,_minmax(17.5rem,_1fr))]
              "
            >
              <li v-for="entry in department.entries" :key="entry.id">
                <CardTeamMember :data="entry" />
              </li>
            </ul>
          </AnimatedContainer>
        </li>
      </ul>
    </Container>
  </ContentSection>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { MainComponentsOverviewTeamFragment, TeamMemberFragment } from '#gql'
import AnimatedContainer from '~/components/AnimatedContainer.vue'

const props = defineProps<{
  data: Enforce<MainComponentsOverviewTeamFragment>
}>()

const { t: $t } = useI18n()

const singleDepartment = computed(() => props.data.selection.value === 'manually' && props.data.department.length === 1)

const title = computed(() => {
  return singleDepartment.value ? props.data.department[0].title : props.data.heading
})

const groupedTeamMembers = computed<
  Array<{
    title: string
    entries: TeamMemberFragment[]
  }>
>(() => {
  const groupedByDepartment = props.data.entries.reduce(
    (acc: Array<{
      id: string
      index: number | undefined
      title: string
      entries: TeamMemberFragment[]
    }>, member: TeamMemberFragment) => {
      const departmentId = member.department?.id ?? $t('no_department')
      const departmentTitle = member.department?.title ?? $t('no_department')
      const departmentIndex = member.department?.__typename === 'Term_Departments_Departments' && member.department.index ? member.department.index : 99

      // Find the existing department group or create a new one
      let departmentGroup = acc.find(group => group.title === departmentTitle)
      if (!departmentGroup) {
        departmentGroup = {
          id: departmentId,
          index: departmentIndex,
          title: departmentTitle,
          entries: [],
        }
        acc.push(departmentGroup)
      }

      // Add the member to the department group
      departmentGroup.entries.push(member)

      return acc
    },
    [] as Array<{
      id: string
      index: number | undefined
      title: string
      entries: TeamMemberFragment[]
    }>,
  )

  const sortedGroupedByDepartment = groupedByDepartment.sort((a, b) => {
    if (a.index === undefined) return 1
    if (b.index === undefined) return -1
    return a.index - b.index
  })

  // Return the grouped data
  return sortedGroupedByDepartment
})
</script>
